/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #*
 #* Copyright NextPro 2018. All Rights Reserved.
 #*===================================================================
 */

import dayjs from 'dayjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';
import { Store } from '@ngrx/store';

import { environment } from 'environments/environment';
import { Constants } from '@shared/models/constants.model';
import { ProductDTO } from '@shared/models/product.model';
import { getAccessState, SharedState } from '@shared/store/reducers';
import { LoadUserAccess, RefreshUserData } from '@shared/store/actions';
import { PurchaseService } from './purchase.service';
import { ToastrTranslationsNotificationService } from './toastr-translations-notification.service';

@Injectable({ providedIn: 'root' })
export class EventsService {
  readonly EMPTY_SPOTS_GAMES = 6;

  constructor(
    private httpClient: HttpClient,
    private purchaseService: PurchaseService,
    private sharedStore: Store<SharedState>,
    private toastrTranslationsNotificationService: ToastrTranslationsNotificationService
  ) {}

  getEventById(payload: any) {
    return this.handleResponse(this.httpClient.get(`${environment.apiHostname}/v1/events/${payload.id}`));
  }

  getEventTeams(eventId: any) {
    return this.handleResponse(this.httpClient.get(`${environment.apiHostname}/v1/teams/event/${eventId}`));
  }

  getEventTeamsGroupedByFilmType(eventId: any) {
    return this.handleResponse(this.httpClient.get(`${environment.apiHostname}/v1/teams/event/${eventId}/grouped`));
  }

  getEventGames(payload: any, take = 0, skip = 0) {
    return this.handleResponse(
      this.httpClient.get(`${environment.apiHostname}/v1/games/event/${payload.id}`, {
        params: { take: take.toString(), skip: skip.toString() },
      })
    );
  }

  getGamesByEvent(payload: any) {
    return this.handleResponse(this.httpClient.get(`${environment.apiHostname}/v1/games/event/filtered/${payload.id}`));
  }

  getEventGamesFiltered(payload: any) {
    /* ignore free content check */
    const ignoreFreeContentParam = payload.ignoreFreeContent ? '&ignoreFreeContent=true' : '';
    return this.handleResponse(
      this.httpClient.get(
        `${environment.apiHostname}/v1/games/event/filtered/${payload.id}?sortParam=${payload.sortParam}&videoId=${payload.videoId}&limit=40${ignoreFreeContentParam}`
      )
    );
  }

  getEventGamesAsGuest(payload: any) {
    return this.handleResponse(this.httpClient.get(`${environment.apiHostname}/v1/games/guest/event/${payload.id}`));
  }

  searchEvents(searchText: string, size = null): Observable<any[]> {
    const searchPayload: any = {
      searchText,
    };
    if (size) {
      searchPayload.from = 0;
      searchPayload.size = size;
    }
    return this.handleResponse(this.httpClient.post(`${environment.apiHostname}/v1/search/events`, searchPayload));
  }

  getLiveEventById(liveEventId: string): Observable<any> {
    return this.handleResponse(this.httpClient.get(`${environment.apiHostname}/v1/live-events/${liveEventId}`));
  }

  // sort on specific field either ascending or descending
  sortEvents(event1: any, event2: any, sortField: string, ascending: boolean) {
    if (ascending) {
      return dayjs(event1[sortField]).isBefore(event2[sortField]) ? -1 : 1;
    } else {
      return dayjs(event1[sortField]).isBefore(event2[sortField]) ? 1 : -1;
    }
  }

  computeAvailableYears(data: any, currentYear: number, refreshYearsList: boolean, availableYears: any[]) {
    let newAvailableYears = availableYears || [];
    if (refreshYearsList || availableYears.length < 2) {
      // aggregate all years
      newAvailableYears = (data || []).reduce(
        (acc, event) => {
          const newYear = dayjs(event.startDate).year();
          if (!acc.find((option) => option.value === newYear)) {
            acc.push({ value: newYear, name: newYear });
          }

          return acc;
        },
        [{ value: '', name: 'All' }]
      );

      newAvailableYears.sort(this.sortYears);
    }

    // set selected year as selected in array of options
    return newAvailableYears.map((option, index) => {
      // if no year is selected then select first option
      const selected = (!currentYear && index === 0) || currentYear === option.value;
      return { ...option, selected };
    });
  }

  reduceEventsToMonthlyGroupedEvents(monthGroupedEvents: any, event: any) {
    const eventMonth = dayjs.utc(event.startDate).format('YYYY-MM-01');
    if (event.active) {
      if (typeof monthGroupedEvents[eventMonth] === 'undefined') {
        monthGroupedEvents[eventMonth] = [event];
      } else {
        monthGroupedEvents[eventMonth].push(event);
      }
    }
    return monthGroupedEvents;
  }

  hasAccessToEvent(user, event): Observable<boolean> {
    if (!user || !event.id) {
      return of(false);
    }

    if (user.type === Constants.USER_TYPE.ADMIN || user.type === Constants.USER_TYPE.HEAD_COACH) {
      return of(true);
    }

    return this.getUserPurchases().pipe(
      map((products) => {
        // check if video has event and if not then allow access
        const isIn2018 = event.startDate
          ? dayjs(event.startDate).isBetween(dayjs('2018-01-01'), dayjs('2019-01-31'))
          : true;
        return products.some((purchase) => {
          if (!purchase.active) {
            return false;
          }

          if (purchase.type === Constants.PURCHASE_TYPE.ALL_EVENTS_2018 && isIn2018) {
            return true;
          } else if (this.isOrderActive(purchase) && purchase.event && purchase.event.id === event.id) {
            return true;
          } else if (purchase.channelId && event.channel && purchase.channelId === event.channel.id) {
            if (!purchase.year) {
              return true;
            } else if (purchase.year && event.startDate && dayjs.utc(event.startDate).year() === purchase.year) {
              return true;
            }
          }
        });
      })
    );
  }

  isAvailableForLibrary(user, eventId, eventChannelId?): Observable<boolean> {
    if (!user) {
      return of(false);
    }
    return this.getUserPurchases().pipe(
      map((products) => {
        /* once event has been added to library because of channel access (no order), show buy button */
        const alreadyAddedToLibrary = products.find(
          (p) => p && p.event && p.event.id === eventId && !p.availableForLibrary && p.active && !p.orderId
        );
        return products.some(
          (p) =>
            (p.event && p.event.id === eventId && p.availableForLibrary && p.active) ||
            (p.channelId && p.channelId === eventChannelId && !alreadyAddedToLibrary)
        );
      })
    );
  }

  getSelectedTeams(eventId, userPurchases) {
    const eventPurchases = userPurchases.filter(
      (purchase) => purchase.active && purchase.teamId && purchase.event?.id === eventId
    );

    if (eventPurchases.length) {
      return {
        hasTeam: true,
        data: eventPurchases.map((eventPurchase) => eventPurchase.teamId),
      };
    }

    return {
      hasTeam: false,
      data: null,
    };
  }

  getUserEvents(user, ignoreDuplicates = false): Observable<any[]> {
    if (!user) {
      return of([]);
    }

    return this.sharedStore.select(getAccessState).pipe(
      tap((state) => {
        if (!state.loading && !state.success) {
          this.sharedStore.dispatch(new LoadUserAccess({}));
        }
      }),
      filter((state) => state.success),
      switchMap((state) => {
        if (!state.products) {
          return of([]);
        }

        return this.prepareUserEvents(user, state.products, ignoreDuplicates);
      })
    );
  }

  // return all purchases (including invitees purchases)
  getUserPurchases(): Observable<ProductDTO[]> {
    return this.sharedStore.select(getAccessState).pipe(
      filter((accessState) => !accessState.loading),
      map((accessState) => accessState.products)
    );
  }

  addToLibrary(user, eventId, sportId, isPurchased?, teamId?) {
    const createNewPurchase = () => {
      const newLibraryPurchase = {
        active: true,
        type: Constants.PURCHASE_TYPE.ONE_EVENT_GF,
        user: { id: user.id },
        event: { id: eventId },
        sport: { id: sportId },
        team: { id: teamId },
      };

      return this.purchaseService.createPurchase(newLibraryPurchase).pipe(
        tap((purchase) => {
          this.sharedStore.dispatch(new RefreshUserData());
          this.toastrTranslationsNotificationService.showNotification('EVENTS.NOTIFICATION_MESSAGES.ADDED_TO_LIBRARY');
        })
      );
    };
    /* if event is already purchased, just add to library */
    if (isPurchased) {
      this.getUserPurchases().subscribe((allPurchases) => {
        const purchases = allPurchases.filter((p) => p.event && p.event.id === eventId && p.availableForLibrary);
        if (purchases && purchases.length) {
          const purchasesUpdate = purchases.map((purchase) => {
            return this.purchaseService.updatePurchase({ ...purchase, availableForLibrary: false });
          });
          return forkJoin(purchasesUpdate).pipe(
            tap(() => {
              this.sharedStore.dispatch(new RefreshUserData());
              this.toastrTranslationsNotificationService.showNotification(
                'EVENTS.NOTIFICATION_MESSAGES.ADDED_TO_LIBRARY'
              );
            })
          );
        }
      });
    }

    /* create new purchase based on given access */
    return createNewPurchase();
  }

  getRosters(eventId: number, teams: any[], joinUser = false) {
    return this.handleResponse(
      this.httpClient.get(
        `${environment.apiHostname}/v1/players/event/${eventId}?teams=${teams[0].id}${
          teams[1] ? ',' + teams[1].id : ''
        }&joinUser=${joinUser}`
      )
    );
  }

  private prepareUserEvents = (user, products: ProductDTO[], ignoreDuplicates): Observable<any[]> => {
    const channelsAccess = {};
    products.forEach((product) => {
      if (product.type === Constants.PURCHASE_TYPE.CHANNEL_GF) {
        channelsAccess[product.channelId] = true;
      }
    });

    return of(
      products.reduce((acc, purchase) => {
        // if purchase access is active (not refunded/revoked)
        // if user didn't purchase all events plan
        // and purchase is one event
        // and purchase is not registered already
        if (
          purchase.active &&
          !purchase.availableForLibrary &&
          (purchase.type === Constants.PURCHASE_TYPE.ONE_EVENT_GF ||
            purchase.type === Constants.PURCHASE_TYPE.ONE_EVENT_GFHR ||
            purchase.type === Constants.PURCHASE_TYPE.COMPLETE_GP ||
            purchase.type === Constants.PURCHASE_TYPE.PERFECT_GP ||
            purchase.type === Constants.PURCHASE_TYPE.ONE_EVENT_GFHRP ||
            purchase.type === Constants.PURCHASE_TYPE.ONE_EVENT_TP)
        ) {
          if (!ignoreDuplicates) {
            // check if event already saved as purchased
            const eventIndex = acc.findIndex((oldEvent) => purchase.event && oldEvent.id === purchase.event.id);
            if (eventIndex !== -1) {
              // if team is selected
              // add selected team for this purchase to the event object
              if (purchase.teamId) {
                const teamIndex = acc[eventIndex].teams.findIndex(
                  (team) => team && purchase.teamId && team.id === purchase.teamId
                );
                // if team is not already in list of teams then add it
                if (purchase.teamId && teamIndex === -1) {
                  acc[eventIndex].teams.push(purchase.teamId);
                }
              }
              return acc;
            }
          }

          acc.push({
            ...purchase.event,
            sport: purchase.sport,
            athlete: purchase.invites?.length
              ? purchase.invites[0].toUser
              : purchase.userId /* keep track of athlete: either as purchaser or invitee */,
            purchase: {
              id: purchase.id,
              type: purchase.type,
              createdAt: purchase.createdAt,
              team: purchase.teamId,
            },
            teams: purchase.teamId ? [purchase.teamId] : [],
          });
        }

        // sort by purchase date descending
        acc.sort((event1, event2) =>
          new Date(event1.purchase.createdAt) < new Date(event2.purchase.createdAt) ? 1 : -1
        );

        return acc;
      }, [])
    );
  };

  private sortYears(a, b) {
    // if the first value is empty or greater than the second one
    if (!a.value || a.value > b.value) {
      return -1;
    }

    // if the second value is empty or greater than the first one
    if (!b.value || a.value < b.value) {
      return 1;
    }

    return 0;
  }

  private isOrderActive(purchase) {
    return purchase.order ? purchase.order.orderType !== Constants.ORDER_TYPE.CANCELED : true;
  }

  private handleResponse(observable: Observable<any>) {
    return observable.pipe(
      map((response: any) => {
        if (response.result.success) {
          return response.data;
        } else {
          throw new Error(response.result.message);
        }
      })
    );
  }
}
